import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  createVideoPlacement,
  deleteVideoPlacement,
  updateVideoPlacement,
} from '../services/admin/admin-video.service';
import { VideoPlacement } from '../common/model/dto/video/video-placement';
import { CreateVideoPlacementRequest } from '../common/model/dto/video/video-requests';
import {
  ADMIN_VIDEO_PLACEMENTS_KEY,
  invalidateVideoQueries,
} from '../common/utils/video-query-cache';

export function useVideoPlacementMutations() {
  const queryClient = useQueryClient();

  const refetchPlacements = () =>
    queryClient.invalidateQueries({ queryKey: [ADMIN_VIDEO_PLACEMENTS_KEY] });

  const create = useMutation({
    mutationFn: (request: CreateVideoPlacementRequest) =>
      createVideoPlacement(request),
    onSuccess: (placement) =>
      queryClient.setQueryData<VideoPlacement[]>(
        [ADMIN_VIDEO_PLACEMENTS_KEY],
        (placements) => placements && [...placements, placement]
      ),
    onSettled: refetchPlacements,
  });

  const updateDescription = useMutation({
    mutationFn: ({ key, description }: { key: string; description: string }) =>
      updateVideoPlacement(key, { description }),
    onSettled: refetchPlacements,
  });

  const remove = useMutation({
    mutationFn: (key: string) => deleteVideoPlacement(key),
    onSettled: () =>
      Promise.all([refetchPlacements(), invalidateVideoQueries(queryClient)]),
  });

  return { create, updateDescription, remove };
}
